import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { 
  BudgetState, 
  BudgetCategory, 
  CustomCategory,
  TaxSettings,
  WageGrowth,
  FilingStatus
} from '@/types'

// Tax calculation helper functions
const calculateFederalTax = (income: number, filingStatus: FilingStatus): number => {
  const brackets = {
    single: [
      { rate: 0.10, max: 11600 },
      { rate: 0.12, max: 47150 },
      { rate: 0.22, max: 100525 },
      { rate: 0.24, max: 191950 },
      { rate: 0.32, max: 243725 },
      { rate: 0.35, max: 609350 },
      { rate: 0.37, max: Infinity },
    ],
    married: [
      { rate: 0.10, max: 23200 },
      { rate: 0.12, max: 94300 },
      { rate: 0.22, max: 201050 },
      { rate: 0.24, max: 383900 },
      { rate: 0.32, max: 487450 },
      { rate: 0.35, max: 731200 },
      { rate: 0.37, max: Infinity },
    ],
  }

  const bracket = brackets[filingStatus] || brackets.single
  let tax = 0
  let remainingIncome = income

  for (let i = 0; i < bracket.length; i++) {
    const currentBracket = bracket[i]
    const previousMax = i > 0 ? bracket[i - 1].max : 0
    const taxableInBracket = Math.min(remainingIncome, currentBracket.max - previousMax)

    if (taxableInBracket > 0) {
      tax += taxableInBracket * currentBracket.rate
      remainingIncome -= taxableInBracket
    }

    if (remainingIncome <= 0) break
  }

  return tax
}

const STATE_TAX_RATES: Record<string, number> = {
  AL: 0.05, AK: 0.0, AZ: 0.0259, AR: 0.055, CA: 0.075, CO: 0.044, CT: 0.0635,
  DE: 0.066, FL: 0.0, GA: 0.0575, HI: 0.04, ID: 0.058, IL: 0.0495, IN: 0.0323,
  IA: 0.0575, KS: 0.057, KY: 0.045, LA: 0.0445, ME: 0.0715, MD: 0.0575, MA: 0.05,
  MI: 0.0425, MN: 0.0685, MS: 0.05, MO: 0.0425, MT: 0.0685, NE: 0.055, NV: 0.0,
  NH: 0.0, NJ: 0.0638, NM: 0.0513, NY: 0.0685, NC: 0.0475, ND: 0.029, OH: 0.0399,
  OK: 0.045, OR: 0.099, PA: 0.0307, RI: 0.0599, SC: 0.07, SD: 0.0, TN: 0.0,
  TX: 0.0, UT: 0.0485, VT: 0.06, VA: 0.0575, WA: 0.0, WV: 0.065, WI: 0.0535, WY: 0.0,
}

const calculateStateTax = (income: number, state: string): number => {
  return income * (STATE_TAX_RATES[state] || 0.05)
}

const calculateSupplementalTax = (income: number): number => {
  const federalRate = income >= 1000000 ? 0.37 : 0.22
  return income * federalRate
}

const initialState: BudgetState = {
  categories: {
    housing: { name: 'Housing', amount: 0, inflationAdjusted: true, type: 'expense' },
    transportation: { name: 'Transportation', amount: 0, inflationAdjusted: true, type: 'expense' },
    food: { name: 'Food', amount: 0, inflationAdjusted: true, type: 'expense' },
    utilities: { name: 'Utilities', amount: 0, inflationAdjusted: true, type: 'expense' },
    healthcare: { name: 'Healthcare', amount: 0, inflationAdjusted: true, type: 'expense' },
    insurance: { name: 'Insurance', amount: 0, inflationAdjusted: true, type: 'expense' },
    entertainment: { name: 'Entertainment', amount: 0, inflationAdjusted: true, type: 'expense' },
    clothing: { name: 'Clothing', amount: 0, inflationAdjusted: true, type: 'expense' },
    education: { name: 'Education', amount: 0, inflationAdjusted: true, type: 'expense' },
    savings: { name: 'Savings', amount: 0, inflationAdjusted: false, type: 'income', autoCalculated: true },
    debt_payment: { name: 'Debt Payments', amount: 0, inflationAdjusted: false, type: 'expense', autoCalculated: true },
    income: { name: 'Gross Income', amount: 0, inflationAdjusted: false, type: 'income' },
  },
  customCategories: [],
  retirementContributions: {
    preTax401k: { amount: 0, percentage: 0, max: 23000, type: 'pre-tax', employerMatch: 0, employerMatchRate: 0 },
    roth401k: { amount: 0, percentage: 0, max: 23000, type: 'post-tax', employerMatch: 0, employerMatchRate: 0 },
    traditionalIRA: { amount: 0, percentage: 0, max: 7000, type: 'pre-tax', employerMatch: 0, employerMatchRate: 0 },
    rothIRA: { amount: 0, percentage: 0, max: 7000, type: 'post-tax', employerMatch: 0, employerMatchRate: 0 },
    hsa: { amount: 0, percentage: 0, max: 4150, type: 'pre-tax', employerMatch: 0, employerMatchRate: 0 },
    otherPreTax: { amount: 0, percentage: 0, max: 0, type: 'pre-tax', employerMatch: 0, employerMatchRate: 0 },
    otherPostTax: { amount: 0, percentage: 0, max: 0, type: 'post-tax', employerMatch: 0, employerMatchRate: 0 },
  },
  employerMatching: {
    enabled: false,
    matchRate: 0,
    matchLimit: 0,
    maxMatchAmount: 0,
    vestingSchedule: 'immediate',
    vestingYears: 0,
  },
  taxSettings: {
    userState: 'CA',
    filingStatus: 'single',
    itemizedDeductions: 0,
    otherDeductions: 0,
    otherIncome: 0,
    capitalGains: 0,
  },
  incomeTypes: {
    salary: { name: 'Base Salary', amount: 0, type: 'salary', taxRate: 'normal' },
    bonus: { name: 'Bonus', amount: 0, type: 'bonus', taxRate: 'supplemental' },
    commission: { name: 'Commission', amount: 0, type: 'commission', taxRate: 'supplemental' },
    overtime: { name: 'Overtime', amount: 0, type: 'overtime', taxRate: 'normal' },
    sideIncome: { name: 'Side Income', amount: 0, type: 'side', taxRate: 'normal' },
    rentalIncome: { name: 'Rental Income', amount: 0, type: 'rental', taxRate: 'normal' },
  },
  wageGrowth: {
    annualGrowthRatePct: 0.03,
    applyTo: ['salary'],
    enabled: false,
  },
  calculations: {
    grossIncome: 0,
    adjustedGrossIncome: 0,
    taxableIncome: 0,
    federalTax: 0,
    stateTax: 0,
    totalTax: 0,
    netIncome: 0,
    totalExpenses: 0,
    freeCashFlow: 0,
    savingsRate: 0,
  },
  _isUpdatingCalculations: false,
}

const budgetSlice = createSlice({
  name: 'budget',
  initialState,
  reducers: {
    updateCategory: (state, action: PayloadAction<{ key: string; updates: Partial<BudgetCategory> }>) => {
      const { key, updates } = action.payload
      if (state.categories[key]) {
        state.categories[key] = { ...state.categories[key], ...updates }
      }
      budgetSlice.caseReducers.updateCalculations(state)
    },
    addCustomCategory: (state, action: PayloadAction<Omit<CustomCategory, 'id'>>) => {
      const category: CustomCategory = {
        id: Date.now(),
        ...action.payload,
      }
      state.customCategories.push(category)
      budgetSlice.caseReducers.updateCalculations(state)
    },
    updateCustomCategory: (state, action: PayloadAction<{ id: number; updates: Partial<CustomCategory> }>) => {
      const { id, updates } = action.payload
      const index = state.customCategories.findIndex((cat) => cat.id === id)
      if (index !== -1) {
        state.customCategories[index] = { ...state.customCategories[index], ...updates }
      }
      budgetSlice.caseReducers.updateCalculations(state)
    },
    removeCustomCategory: (state, action: PayloadAction<number>) => {
      state.customCategories = state.customCategories.filter((cat) => cat.id !== action.payload)
      budgetSlice.caseReducers.updateCalculations(state)
    },
    updateRetirementContribution: (state, action: PayloadAction<{ type: keyof typeof initialState.retirementContributions; amount: number }>) => {
      const { type, amount } = action.payload
      if (state.retirementContributions[type]) {
        state.retirementContributions[type].amount = Math.min(amount, state.retirementContributions[type].max)
      }
      budgetSlice.caseReducers.updateCalculations(state)
    },
    updateRetirementContributionPercentage: (state, action: PayloadAction<{ type: keyof typeof initialState.retirementContributions; percentage: number }>) => {
      const { type, percentage } = action.payload
      if (state.retirementContributions[type]) {
        state.retirementContributions[type].percentage = percentage
      }
      budgetSlice.caseReducers.updateCalculations(state)
    },
    updateEmployerMatching: (state, action: PayloadAction<{ setting: keyof typeof initialState.employerMatching; value: unknown }>) => {
      const { setting, value } = action.payload
      // @ts-expect-error - Dynamic assignment
      state.employerMatching[setting] = value
      budgetSlice.caseReducers.updateCalculations(state)
    },
    updateTaxSettings: (state, action: PayloadAction<{ setting: keyof TaxSettings; value: unknown }>) => {
      const { setting, value } = action.payload
      // @ts-expect-error - Dynamic assignment
      state.taxSettings[setting] = value
      budgetSlice.caseReducers.updateCalculations(state)
    },
    updateIncomeType: (state, action: PayloadAction<{ type: string; amount: number }>) => {
      const { type, amount } = action.payload
      if (state.incomeTypes[type]) {
        state.incomeTypes[type].amount = amount
      } else {
        state.incomeTypes[type] = {
          name: type.charAt(0).toUpperCase() + type.slice(1).replace(/([A-Z])/g, ' $1'),
          amount,
          type,
          taxRate: 'normal',
        }
      }
      budgetSlice.caseReducers.updateCalculations(state)
    },
    updateWageGrowth: (state, action: PayloadAction<{ setting: keyof WageGrowth; value: unknown }>) => {
      const { setting, value } = action.payload
      // @ts-expect-error - Dynamic assignment
      state.wageGrowth[setting] = value
    },
    syncDebtPayments: (state, action: PayloadAction<{ totalMonthlyPayments: number; realEstatePayments?: number }>) => {
      const { totalMonthlyPayments, realEstatePayments = 0 } = action.payload
      const totalDebtPayments = totalMonthlyPayments + realEstatePayments

      if (state.categories.debt_payment && state.categories.debt_payment.amount !== totalDebtPayments) {
        state.categories.debt_payment = {
          ...state.categories.debt_payment,
          amount: totalDebtPayments,
        }
        budgetSlice.caseReducers.updateCalculations(state)
      }
    },
    setBudgetData: (state, action: PayloadAction<Partial<BudgetState>>) => {
      const data = action.payload
      if (data.categories) {
        Object.keys(data.categories).forEach((key) => {
          if (state.categories[key]) {
            state.categories[key] = { ...state.categories[key], ...data.categories![key] }
          } else {
            state.categories[key] = data.categories![key]
          }
        })
      }
      if (data.customCategories) state.customCategories = data.customCategories
      if (data.retirementContributions) {
        Object.keys(data.retirementContributions).forEach((key) => {
          const k = key as keyof typeof state.retirementContributions
          if (state.retirementContributions[k]) {
            state.retirementContributions[k] = { ...state.retirementContributions[k], ...data.retirementContributions![k] }
          }
        })
      }
      if (data.employerMatching) state.employerMatching = { ...state.employerMatching, ...data.employerMatching }
      if (data.taxSettings) state.taxSettings = { ...state.taxSettings, ...data.taxSettings }
      if (data.incomeTypes) {
        Object.keys(data.incomeTypes).forEach((key) => {
          state.incomeTypes[key] = { ...state.incomeTypes[key], ...data.incomeTypes![key] }
        })
      }
      if (data.wageGrowth) state.wageGrowth = { ...state.wageGrowth, ...data.wageGrowth }
      if (data.calculations) state.calculations = { ...state.calculations, ...data.calculations }
      budgetSlice.caseReducers.updateCalculations(state)
    },
    updateCalculations: (state) => {
      if (state._isUpdatingCalculations) return
      state._isUpdatingCalculations = true

      try {
        // Calculate total income from all income types
        const totalIncome = Object.values(state.incomeTypes).reduce((sum, inc) => sum + inc.amount, 0)

        const regularIncome = Object.values(state.incomeTypes)
          .filter((inc) => inc.taxRate === 'normal')
          .reduce((sum, inc) => sum + inc.amount, 0)

        const supplementalIncome = Object.values(state.incomeTypes)
          .filter((inc) => inc.taxRate === 'supplemental')
          .reduce((sum, inc) => sum + inc.amount, 0)

        const grossIncome = totalIncome

        // Calculate pre-tax contributions from percentages
        Object.keys(state.retirementContributions).forEach((key) => {
          const k = key as keyof typeof state.retirementContributions
          const contrib = state.retirementContributions[k]
          if (contrib.type === 'pre-tax' && contrib.percentage > 0) {
            const annualFromPct = grossIncome * contrib.percentage
            state.retirementContributions[k].amount = Math.min(annualFromPct, contrib.max || Infinity)
          }
        })

        // Employer matching for 401k
        Object.keys(state.retirementContributions).forEach((key) => {
          const k = key as keyof typeof state.retirementContributions
          const is401k = k === 'preTax401k' || k === 'roth401k'
          if (state.employerMatching.enabled && is401k) {
            const employeeContribution = state.retirementContributions[k].amount
            const employerMatchAmount = Math.min(
              employeeContribution * (state.employerMatching.matchRate || 0),
              state.employerMatching.maxMatchAmount || Infinity
            )
            state.retirementContributions[k].employerMatch = employerMatchAmount
            state.retirementContributions[k].employerMatchRate = state.employerMatching.matchRate
          } else {
            state.retirementContributions[k].employerMatch = 0
            state.retirementContributions[k].employerMatchRate = 0
          }
        })

        const preTaxEmployeeContributions = Object.values(state.retirementContributions)
          .filter((c) => c.type === 'pre-tax')
          .reduce((sum, c) => sum + c.amount, 0)

        const adjustedGrossIncome = grossIncome - preTaxEmployeeContributions + state.taxSettings.otherIncome

        const standardDeduction = state.taxSettings.filingStatus === 'single' ? 14600 : 29200
        const totalDeductions = Math.max(standardDeduction, state.taxSettings.itemizedDeductions) + state.taxSettings.otherDeductions
        const taxableRegularIncome = Math.max(0, regularIncome - preTaxEmployeeContributions - totalDeductions)

        const federalTaxRegular = calculateFederalTax(taxableRegularIncome, state.taxSettings.filingStatus)
        const stateTaxRegular = calculateStateTax(taxableRegularIncome, state.taxSettings.userState)

        const federalTaxSupplemental = calculateSupplementalTax(supplementalIncome)
        const stateTaxSupplemental = calculateStateTax(supplementalIncome, state.taxSettings.userState)

        const federalTax = federalTaxRegular + federalTaxSupplemental
        const stateTax = stateTaxRegular + stateTaxSupplemental
        const totalTax = federalTax + stateTax

        const netIncome = grossIncome - totalTax

        // Post-tax contributions
        const monthlyNetIncomeBeforePostTax = netIncome / 12
        Object.keys(state.retirementContributions).forEach((key) => {
          const k = key as keyof typeof state.retirementContributions
          const contrib = state.retirementContributions[k]
          if (contrib.type === 'post-tax' && contrib.percentage > 0) {
            const annualFromPct = monthlyNetIncomeBeforePostTax * contrib.percentage * 12
            state.retirementContributions[k].amount = Math.min(annualFromPct, contrib.max || Infinity)
          }
        })

        const totalExpenses = Object.values(state.categories)
          .filter((cat) => cat.type === 'expense' && !cat.autoCalculated)
          .reduce((sum, cat) => sum + cat.amount * 12, 0)

        const customExpenses = state.customCategories
          .filter((cat) => cat.type === 'expense')
          .reduce((sum, cat) => sum + cat.amount * 12, 0)

        const totalAllExpenses = totalExpenses + customExpenses

        const postTaxEmployeeContributions = Object.values(state.retirementContributions)
          .filter((c) => c.type === 'post-tax')
          .reduce((sum, c) => sum + c.amount, 0)

        const freeCashFlow = netIncome - totalAllExpenses - preTaxEmployeeContributions - postTaxEmployeeContributions

        const savingsRate = grossIncome > 0 ? (freeCashFlow / grossIncome) * 100 : 0

        const newSavingsAmount = Math.max(0, freeCashFlow / 12)

        state.calculations = {
          grossIncome,
          adjustedGrossIncome,
          taxableIncome: taxableRegularIncome + supplementalIncome,
          federalTax,
          stateTax,
          totalTax,
          netIncome,
          totalExpenses: totalAllExpenses,
          freeCashFlow,
          savingsRate,
        }

        if (state.categories.savings) {
          state.categories.savings.amount = newSavingsAmount
        }

        if (state.categories.income) {
          state.categories.income.amount = grossIncome / 12
        }
      } finally {
        state._isUpdatingCalculations = false
      }
    },
  },
})

export const {
  updateCategory,
  addCustomCategory,
  updateCustomCategory,
  removeCustomCategory,
  updateRetirementContribution,
  updateRetirementContributionPercentage,
  updateEmployerMatching,
  updateTaxSettings,
  updateIncomeType,
  updateWageGrowth,
  syncDebtPayments,
  setBudgetData,
} = budgetSlice.actions

export default budgetSlice.reducer
